import { createSelector, EntityId } from '@reduxjs/toolkit';
import { DeviceVariantsEnum } from '@ubnt/link-core/enums';
import { deviceInstanceGetter } from '@ubnt/wifi-calculator/device/deviceInstanceGetter';
import { resolveMountHeight } from '@ubnt/wifi-calculator/mount/resolveMountHeight';
import {
  countrySupportsBand,
  getBandByVariant,
  is5GHzBandByVariant,
} from '@ubnt/wifi-calculator/utils/regulatoryLimits';
import { isEnumValue, isSupportedVariant, resolveChannelWidth, resolveVariant } from '@ubnt/wifi-calculator/utils';
import {
  devicesCustomClientDeviceInstanceMapGetter,
  devicesCustomClientDeviceInstanceMapSelector,
  devicesCustomClientDevicesDataSelector,
  devicesDeviceAfcGetter,
  devicesDeviceAntennaNameGetter,
  devicesDeviceChannelWidthGetter,
  devicesDeviceFrequencyGetter,
  devicesDeviceIdGetter,
  devicesDeviceIsOutdoorModeGetter,
  devicesDeviceMapSelector,
  devicesDeviceMloGetter,
  devicesDeviceMountGetter,
  devicesDeviceNameGetter,
  devicesDeviceVariantGetter,
  devicesMeshDeviceVariantGetter,
  ApStateDevice,
  DevicesMap,
} from 'modules/devices/slice';
import { isAp } from 'modules/devices/utils';
import { settingsCeilingHeightSelector, settingsCountrySelector } from 'modules/settings/slice';
import {
  linksDeviceChannelWidthGetter,
  linksDeviceFrequencyGetter,
  linksLinkApIdGetter,
  linksLinkClientIdGetter,
  linksLinkIsMeshGetter,
  linksLinkMapSelector,
  linksMeshUplinkApIdGetter,
  linksMeshUplinkByApIdGetter,
  type StateLink,
} from 'modules/links/slice';
import { CLIENT_ID } from 'modules/devices/constants';
import { secondArg } from 'app/store/utils';

interface CoverageApParams {
  apId: string;
  apDeviceName: string;
  clientDeviceName: string;
  antennaName: string;
  mlo: boolean;
  mount: NonNullable<ApStateDevice['mount']>;
  mountHeight: number;
  variant: ValuesOf<typeof DeviceVariantsEnum>; // active variant
  variants: {
    variant: ValuesOf<typeof DeviceVariantsEnum>;
    channelWidth: number;
    apChannelWidth: number;
    frequency: number;
  }[];
  country?: string;
  externalDeviceMap?: Record<string, LinkCore.DeviceData>;
  afc: boolean;
  isOutdoor: boolean;
  floors?: { floor: -1 | 0 | 1; height: number; attenuation: number }[];
}

const linksCoverageParamsGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  country?: string,
  externalDeviceDataMap?: Record<string, LinkCore.DeviceData>,
  ceilingHeight?: number
) => {
  const coverageParams: CoverageApParams[] = [];

  const clientDevice = devicesMap[CLIENT_ID];
  const clientDeviceName = devicesDeviceNameGetter(clientDevice);
  const externalDeviceMap =
    externalDeviceDataMap ? devicesCustomClientDeviceInstanceMapGetter(externalDeviceDataMap) : {};
  const clientInstance = deviceInstanceGetter(clientDeviceName, undefined, externalDeviceMap);

  Object.values(devicesMap).forEach((device) => {
    if (!isAp(device)) {
      return;
    }

    const variant = devicesDeviceVariantGetter(device);
    const apDeviceName = devicesDeviceNameGetter(device);
    const apInstance = deviceInstanceGetter(apDeviceName, undefined, externalDeviceMap);

    if (
      variant == null ||
      !isSupportedVariant(apInstance.variants, variant) ||
      !isSupportedVariant(clientInstance.variants, variant)
    ) {
      return;
    }

    const mlo = devicesDeviceMloGetter(device);
    const mount = devicesDeviceMountGetter(device);
    const mountHeight = resolveMountHeight(mount, undefined, ceilingHeight);
    const apId = devicesDeviceIdGetter(device);
    const afc = devicesDeviceAfcGetter(device);
    const isOutdoor = devicesDeviceIsOutdoorModeGetter(device);

    const apCoverageParams: CoverageApParams = {
      apId,
      apDeviceName,
      clientDeviceName,
      antennaName: devicesDeviceAntennaNameGetter(device),
      mlo,
      mount,
      mountHeight,
      variant,
      variants: [],
      country,
      externalDeviceMap: externalDeviceDataMap,
      afc,
      isOutdoor,
      floors: ceilingHeight != null ? [{ floor: 0 as const, height: ceilingHeight, attenuation: 0 }] : undefined,
    };

    apInstance.variants.forEach((variant) => {
      const band = getBandByVariant(variant);
      if (
        !isSupportedVariant(clientInstance.variants, variant) ||
        !isEnumValue(DeviceVariantsEnum, variant) ||
        (country && !countrySupportsBand(country, band, isOutdoor, afc))
      ) {
        return;
      }

      const apVariantInstance = deviceInstanceGetter(apDeviceName, variant, externalDeviceMap);
      const clientVariantInstance = deviceInstanceGetter(
        clientDeviceName,
        resolveVariant(clientInstance.variants, variant),
        externalDeviceMap
      );
      const meshUplink = linksMeshUplinkByApIdGetter(linksMap, apId);
      const useUplinkData =
        meshUplink && is5GHzBandByVariant(variant) && variant === devicesMeshDeviceVariantGetter(devicesMap);
      const meshUplinkInstance =
        meshUplink && deviceInstanceGetter(devicesDeviceNameGetter(devicesMap[linksLinkApIdGetter(meshUplink)]));

      let apChannelWidth =
        useUplinkData ?
          linksDeviceChannelWidthGetter(
            devicesMap,
            linksMap,
            meshUplink,
            resolveVariant(meshUplinkInstance?.variants ?? apInstance.variants, variant),
            externalDeviceMap
          )
        : devicesDeviceChannelWidthGetter(device, variant);

      if (useUplinkData) {
        apChannelWidth = resolveChannelWidth(apVariantInstance.channelWidths, apChannelWidth);
      }

      const channelWidth = resolveChannelWidth(clientVariantInstance.channelWidths, apChannelWidth);

      const frequency =
        useUplinkData ?
          linksDeviceFrequencyGetter(devicesMap, linksMap, meshUplink, variant, country, externalDeviceMap)
        : devicesDeviceFrequencyGetter(device, variant, country, externalDeviceMap);

      if (channelWidth != null && frequency != null && apChannelWidth != null) {
        apCoverageParams.variants.push({
          apChannelWidth,
          channelWidth,
          frequency,
          variant,
        });
      }
    });

    coverageParams.push(apCoverageParams);
  });

  return coverageParams;
};

export const linksCoverageParamsSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  settingsCountrySelector,
  devicesCustomClientDevicesDataSelector,
  settingsCeilingHeightSelector,
  linksCoverageParamsGetter
);

const NO_CHANNEL_WIDTH_WARNING = { labelTooltip: undefined, labelTooltipProps: undefined };

export const linksChannelWidthWarningSelector = createSelector(
  secondArg<StateLink['id']>,
  linksLinkMapSelector,
  devicesDeviceMapSelector,
  devicesCustomClientDeviceInstanceMapSelector,
  (linkId, linksMap, devicesMap, externalDeviceMap) => {
    const activeLink = linksMap[linkId];
    const linkIsMesh = linksLinkIsMeshGetter(devicesMap, activeLink);
    const apId = linksLinkApIdGetter(activeLink);
    const apDevice = devicesMap[apId];
    const variant = devicesDeviceVariantGetter(apDevice);
    const channelWidth = devicesDeviceChannelWidthGetter(apDevice, variant);

    const clientId = linksLinkClientIdGetter(activeLink);
    const clientDevice = devicesMap[clientId];
    const clientName = devicesDeviceNameGetter(clientDevice);
    const defaultClientInstance = deviceInstanceGetter(clientName, undefined, externalDeviceMap);
    const clientInstance = deviceInstanceGetter(
      clientName,
      resolveVariant(defaultClientInstance.variants, variant),
      externalDeviceMap
    );
    const clientChannelWidths = clientInstance.channelWidths;
    const controlsUplinkAp = is5GHzBandByVariant(variant) && linksMeshUplinkApIdGetter(linksMap, activeLink);

    if (controlsUplinkAp) {
      return NO_CHANNEL_WIDTH_WARNING;
    }

    if (channelWidth && !clientChannelWidths.includes(channelWidth)) {
      const labelTooltip = `The selected AP channel width is not supported by ${clientName}. 
        Supported channel widths for the ${linkIsMesh ? 'mesh' : 'client'} are: ${clientChannelWidths.map((width) => `${width} MHz`).join(', ')}.`;

      const labelTooltipProps = { severity: 'warning' as const };

      return { labelTooltip, labelTooltipProps };
    }

    return NO_CHANNEL_WIDTH_WARNING;
  }
);
